import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

const Lightbox = ({ selectedImage, images, onClose }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isVideoPlaying, setIsVideoPlaying] = useState(false);
  
  // Update current index when image changes
  useEffect(() => {
    if (selectedImage && images && images.length > 0) {
      const index = images.findIndex(img => 
        img.src === selectedImage.src || img.id === selectedImage.id
      );
      setCurrentIndex(index >= 0 ? index : 0);
      setIsVideoPlaying(false); // Reset video playing state
    }
  }, [selectedImage, images]);

  // Handle keyboard navigation
  useEffect(() => {
    if (!selectedImage || !images || images.length === 0) return;
    
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        onClose();
      } else if (e.key === 'ArrowRight') {
        next();
      } else if (e.key === 'ArrowLeft') {
        prev();
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selectedImage, currentIndex, images]);

  // Early return if no image or invalid data
  if (!selectedImage || !images || images.length === 0) return null;

  const next = () => {
    setCurrentIndex((prev) => (prev + 1) % images.length);
    setIsVideoPlaying(false); // Stop video when navigating
  };

  const prev = () => {
    setCurrentIndex((prev) => (prev - 1 + images.length) % images.length);
    setIsVideoPlaying(false); // Stop video when navigating
  };

  // Safe access to current image with fallback
  const current = images[currentIndex] || selectedImage;

  // Handle video play button click
  const handleVideoPlay = () => {
    setIsVideoPlaying(true);
  };

  // Handle video close (back to thumbnail)
  const handleVideoClose = () => {
    setIsVideoPlaying(false);
  };

  const renderMediaContent = (item) => {
    if (item.type === 'video') {
      if (isVideoPlaying) {
        return (
          <div style={styles.videoPlayerContainer}>
            <video 
              src={item.src} 
              controls 
              autoPlay
              style={styles.video}
              onEnded={() => setIsVideoPlaying(false)}
            >
              Your browser does not support the video tag.
            </video>
            {/* Mobile video close button */}
            <button 
              style={styles.videoCloseButton} 
              onClick={handleVideoClose}
              aria-label="Close video"
            >
              <i className="fas fa-times" style={styles.videoCloseIcon}></i>
            </button>
          </div>
        );
      } else {
        return (
          <div style={styles.videoContainer} onClick={handleVideoPlay}>
            <img 
              src={item.thumbnail} 
              alt={item.alt || 'Video Thumbnail'} 
              style={styles.image} 
            />
            <div style={styles.videoOverlay}>
              <div style={styles.playButton}>
                <i className="fas fa-play" style={styles.playIcon}></i>
              </div>
              <div style={styles.videoLabel}>
                <i className="fas fa-video" style={styles.videoIcon}></i>
                <span>Click to Play Video</span>
              </div>
            </div>
          </div>
        );
      }
    } else {
      return (
        <img 
          src={item.src} 
          alt={item.alt || 'Gallery Image'} 
          style={styles.image} 
        />
      );
    }
  };

  return (
    <AnimatePresence>
      <motion.div 
        style={styles.lightbox}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={onClose}
      >
        <button style={styles.closeButton} onClick={onClose}>
          <i className="fa fa-times"></i>
        </button>
        
        <motion.div 
          style={styles.content}
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.9, opacity: 0 }}
          onClick={(e) => e.stopPropagation()}
        >
          {renderMediaContent(current)}
        </motion.div>
        
        {/* Only show navigation if there are multiple images and video is not playing */}
        {images.length > 1 && !isVideoPlaying && (
          <div style={styles.navigation}>
            <button style={styles.navButtonPrev} onClick={(e) => {
              e.stopPropagation();
              prev();
            }}>
              <i className="fa fa-chevron-left"></i>
            </button>
            <button style={styles.navButtonNext} onClick={(e) => {
              e.stopPropagation();
              next();
            }}>
              <i className="fa fa-chevron-right"></i>
            </button>
          </div>
        )}
        
        {/* Media counter - hide when video is playing on mobile */}
        {!isVideoPlaying && (
          <div style={styles.counter}>
            {currentIndex + 1} / {images.length}
            {current.type === 'video' && (
              <span style={styles.mediaType}>
                <i className="fas fa-video" style={{ marginLeft: '8px' }}></i>
              </span>
            )}
          </div>
        )}
      </motion.div>
    </AnimatePresence>
  );
};

const styles = {
  lightbox: {
    position: 'fixed',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    backgroundColor: 'rgba(0, 0, 0, 0.9)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
    padding: '30px',
  },
  content: {
    position: 'relative',
    maxWidth: '90%',
    maxHeight: '90%',
    boxShadow: '0 0 25px rgba(0, 0, 0, 0.5)',
    borderRadius: '8px',
    overflow: 'hidden',
  },
  image: {
    maxWidth: '100%',
    maxHeight: '80vh',
    display: 'block',
    objectFit: 'contain',
  },
  video: {
    maxWidth: '100%',
    maxHeight: '80vh',
    display: 'block',
    objectFit: 'contain',
  },
  videoPlayerContainer: {
    position: 'relative',
    width: '100%',
    height: '100%',
  },
  videoContainer: {
    position: 'relative',
    cursor: 'pointer',
  },
  videoOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'rgba(0, 0, 0, 0.3)',
  },
  playButton: {
    width: '80px',
    height: '80px',
    borderRadius: '50%',
    backgroundColor: 'rgba(220, 53, 69, 0.9)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'all 0.3s ease',
    border: '3px solid rgba(255, 255, 255, 0.8)',
  },
  playIcon: {
    fontSize: '28px',
    color: 'white',
    marginLeft: '4px',
  },
  videoLabel: {
    position: 'absolute',
    bottom: '20px',
    left: '50%',
    transform: 'translateX(-50%)',
    backgroundColor: 'rgba(0, 0, 0, 0.8)',
    color: 'white',
    padding: '8px 16px',
    borderRadius: '20px',
    fontSize: '0.9rem',
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
  },
  videoIcon: {
    fontSize: '0.8rem',
  },
  videoCloseButton: {
    position: 'absolute',
    top: '10px',
    right: '10px',
    backgroundColor: 'rgba(0, 0, 0, 0.7)',
    border: 'none',
    width: '40px',
    height: '40px',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    zIndex: 10,
    '@media (max-width: 768px)': {
      top: '15px',
      right: '15px',
      width: '45px',
      height: '45px',
    }
  },
  videoCloseIcon: {
    fontSize: '16px',
    color: 'white',
  },
  caption: {
    backgroundColor: 'rgba(0, 0, 0, 0.8)',
    color: 'white',
    padding: '15px',
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
  },
  captionTitle: {
    margin: '0 0 5px',
    fontSize: '1.2rem',
    fontWeight: '600',
    display: 'flex',
    alignItems: 'center',
  },
  captionText: {
    margin: 0,
    fontSize: '0.9rem',
    opacity: 0.9,
  },
  closeButton: {
    position: 'absolute',
    top: '20px',
    right: '20px',
    backgroundColor: 'rgba(255, 255, 255, 0.2)',
    border: 'none',
    width: '40px',
    height: '40px',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: 'white',
    fontSize: '1.2rem',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    zIndex: 1001,
  },
  navigation: {
    position: 'absolute',
    bottom: '15px',
    right: '15px',
    display: 'flex',
    gap: '10px',
  },
  navButtonPrev: {
    backgroundColor: 'rgba(255, 255, 255, 0.2)',
    border: 'none',
    width: '40px',
    height: '40px',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: 'white',
    fontSize: '1.2rem',
    cursor: 'pointer',
    transition: 'background 0.2s ease',
  },
  navButtonNext: {
    backgroundColor: 'rgba(255, 255, 255, 0.2)',
    border: 'none',
    width: '40px',
    height: '40px',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: 'white',
    fontSize: '1.2rem',
    cursor: 'pointer',
    transition: 'background 0.2s ease',
  },
  counter: {
    position: 'absolute',
    bottom: '15px',
    left: '15px',
    backgroundColor: 'rgba(0, 0, 0, 0.6)',
    color: 'white',
    padding: '5px 10px',
    borderRadius: '15px',
    fontSize: '0.9rem',
    display: 'flex',
    alignItems: 'center',
  },
  mediaType: {
    display: 'flex',
    alignItems: 'center',
  },
};

export default Lightbox;